import * as React from 'react';
import * as navigate from 'app/area/shared/navigation';
import { Paper, PromptConfirmation } from 'ui';
import { withPrompt, PromptProps, SpinLoader } from 'app/components';
import { STATE } from 'app/constants';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { containerFactory, DefaultContainerProps } from 'app/infrastructure/redux';
import { hasTranslationsLoaded, translationChanged } from 'app/infrastructure/translate/selectors';
import { OptionsProps, withOptions } from 'app/components/options-handler';
import { favouriteAdminTranslation } from '../constants';
import { FavouriteAdminState } from '../types-store';
import { FavouriteFormSections } from './components/favourite-form-sections';
import { actionCreators } from './actions';
import { FavouriteFormSelectorProps, selectors } from './selectors';
import { FavouriteItem } from './types-store';
import { getFavouriteAdminAccess } from 'app/area/favourite-administration/get-access';
import { scrollToTop } from 'app/infrastructure/platform/routing';
import { replaceTemplateString } from 'app/infrastructure/platform';
import { getFavouriteName } from 'app/area/favourite-administration/form/components/helpers';

type FavouriteFormProps = DefaultContainerProps
  & OptionsProps
  & PromptProps
  & FavouriteAdminState
  & FavouriteFormSelectorProps
  & typeof actionCreators;

class FavouriteFormComponent extends React.PureComponent<FavouriteFormProps> {
  public static statePath: string = STATE.favouriteAdministration;

  public componentDidMount() {
    scrollToTop();
    this.props.loadLanguageOptions();

    if (hasTranslationsLoaded(this.props.translation)) {
      this.initForm();
    }
  }

  public componentDidUpdate(prevProps: FavouriteFormProps) {

    if (this.props.id !== prevProps.id ||
      translationChanged(this.props.translation, prevProps.translation)) {

      this.initForm();
    }
  }

  public componentWillUnmount() {
    this.props.clearForm();
  }

  private initForm = () => {
    const { id, sharedState,
      loadFavourite, displayError, translation } = this.props;

    const favouriteAccess = getFavouriteAdminAccess(sharedState);
    if (!favouriteAccess) {
      displayError(translation.no_access_to_favourite_admin);
      navigate.toStartupView(true);
      return;
    }

    if (isDefinedIncludingZero(id)) {
      loadFavourite(id);
    }
  };

  private saveFavourite = (favourite: FavouriteItem, addOwnersToDescendants?: boolean) => {
    const { saveFavourite, translation } = this.props;

    const successMessage = isDefinedIncludingZero(favourite?.id) && translation.favourite_updated;

    saveFavourite(favourite, successMessage, translation.favourite_validation_error, addOwnersToDescendants);
  };

  private deleteFavourite = () => {
    const {
      id, form: { favouriteItem } = {}, sharedState: {
        language: { value: mainLanguage } = {}
      } = {}, translation, deleteFavourite
    } = this.props;

    const favouriteName = getFavouriteName(favouriteItem, mainLanguage);

    deleteFavourite(
      id,
      replaceTemplateString(translation.favourite_deleted, favouriteName)
    );
  };

  private clearParent = () => this.props.updateItem({ parent: undefined });

  public render() {
    const { section, sharedState, form, translation,
      options, shouldPrompt, loadOptions, deleteSchedule,
      updateItem, changeLanguage, copyLanguageToAll,
      changeLongDescription, copyLongDescriptionToAll,
      updateValidation, resetValidation, displayError
    } = this.props;

    const mainLanguage = sharedState?.language?.value;
    const locale = sharedState?.locale || '';

    return <div className="entity-view">
      <Paper componentId="code-base-paper" className={undefined}>
        {!!this.props.shouldDisplayModal &&
          <PromptConfirmation
            title={translation.cancel_changes}
            description={translation.are_you_sure}
            continueText={translation.undo_all}
            cancelText={translation.go_back}
            onContinue={this.props.continue}
            onCancel={this.props.cancel} />}

        {!form.languageOptions || !form.favouriteItem || form.fetching
          ? <SpinLoader />
          : <FavouriteFormSections
            formState={form}
            mainLanguage={mainLanguage}
            shouldPrompt={shouldPrompt}
            saveFavourite={this.saveFavourite}
            deleteFavourite={this.deleteFavourite}
            clearParent={this.clearParent}
            updateItem={updateItem}
            changeLanguage={changeLanguage}
            copyLanguageToAll={copyLanguageToAll}

            changeLongDescription={changeLongDescription}
            copyLongDescriptionToAll={copyLongDescriptionToAll}

            options={options}
            loadOptions={loadOptions}

            deleteSchedule={deleteSchedule}
            scheduleDeleting={form.scheduleDeleting}

            updateValidation={updateValidation}
            resetValidation={resetValidation}
            displayError={displayError}
            section={section}
            locale={locale}
            translation={translation} />}
      </Paper>
    </div>;
  }
}

const container = containerFactory(
  withPrompt(withOptions(FavouriteFormComponent)),
  actionCreators, selectors, favouriteAdminTranslation
);

export { container as FavouriteForm };
